import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, DollarSignIcon, BuildingIcon, CalendarIcon, FileTextIcon, CheckCircleIcon } from 'lucide-react';
export function BillFinancingWizard() {
  const {
    t
  } = useTranslation('bills');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    loanPackage: '',
    financingAmount: '',
    requestedTerm: '30',
    purpose: '',
    additionalNotes: ''
  });
  // Mock bill data
  const bill = {
    id: id,
    billNumber: 'BILL-2024-002',
    supplierName: 'Spice Traders Co',
    amount: 87500,
    dueDate: '2024-02-20'
  };
  const dfccLoanPackages = [{
    id: 'standard',
    name: 'Standard Financing',
    interestRate: '12%',
    maxTerm: '90 days',
    processingTime: '48 hours',
    description: 'Ideal for regular business operations with flexible terms'
  }, {
    id: 'premium',
    name: 'Premium Financing',
    interestRate: '10.5%',
    maxTerm: '120 days',
    processingTime: '24 hours',
    description: 'Lower rates for established businesses with priority processing'
  }, {
    id: 'express',
    name: 'Express Financing',
    interestRate: '14%',
    maxTerm: '60 days',
    processingTime: '12 hours',
    description: 'Quick approval for urgent cash flow needs'
  }, {
    id: 'sme',
    name: 'SME Growth Package',
    interestRate: '11%',
    maxTerm: '90 days',
    processingTime: '36 hours',
    description: 'Special rates for small and medium enterprises'
  }];
  const handleSubmit = () => {
    console.log('Submitting financing request:', {
      billId: id,
      ...formData
    });
    navigate('/bills');
  };
  const selectedPackage = dfccLoanPackages.find(p => p.id === formData.loanPackage);
  return <div className="max-w-4xl mx-auto space-y-6" data-id="element-3944" data-mp="3d3skulrk11gi">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-3945" data-mp="3d1y83uqfsfni">
        <button onClick={() => navigate(`/bill-detail/${id}`)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-3946" data-mp="393ih7fr8in4t">
          <ArrowLeftIcon size={20} data-id="element-3947" data-mp="2z4jgcjcpnvsd" />
          {t('pages.BillFinancingWizard.back_to_bill')}
        </button>
      </div>
      {/* Progress Steps */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-3948" data-mp="2u6rd4t6m8n8v">
        <div className="flex items-center justify-between mb-8" data-id="element-3949" data-mp="2pe7yru03to30">
          <div className="flex-1" data-id="element-3950" data-mp="2lcw6thtrcqrn">
            <div className={`flex items-center ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-3951" data-mp="2lc3bdd091not">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-3952" data-mp="2gfcvoqscwu3x">
                1
              </div>
              <div className="ml-3" data-id="element-3953" data-mp="26hoylu7a3ail">
                <div className="text-sm font-medium" data-id="element-3954" data-mp="1wc6v7wvljeg6">
                  {t('pages.BillApprovalWizard.bill_details')}
                </div>
              </div>
            </div>
          </div>
          <div className={`flex-1 h-1 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-3955" data-mp="26a4hwfxt536o"></div>
          <div className="flex-1" data-id="element-3956" data-mp="2b9hfnvq7vuo1">
            <div className={`flex items-center ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-3957" data-mp="2b8fsisvxfhpy">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-3958" data-mp="1djcpltjv4yom">
                2
              </div>
              <div className="ml-3" data-id="element-3959" data-mp="1sa3i73ta41ss">
                <div className="text-sm font-medium" data-id="element-3960" data-mp="1sb53s27x86or">
                  {t('pages.BillFinancingWizard.select_provider')}
                </div>
              </div>
            </div>
          </div>
          <div className={`flex-1 h-1 ${step >= 3 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-3961" data-mp="zsr4dy6xscnk"></div>
          <div className="flex-1" data-id="element-3962" data-mp="31jq9qdz6qh0k">
            <div className={`flex items-center ${step >= 3 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-3963" data-mp="31glwkr3qsaxr">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-3964" data-mp="3b99s7iizk8uw">
                3
              </div>
              <div className="ml-3" data-id="element-3965" data-mp="3k8ohienf2ddt">
                <div className="text-sm font-medium" data-id="element-3966" data-mp="3k7mvxg8ry8hu">
                  {t('pages.BillFinancingWizard.financing_terms')}
                </div>
              </div>
            </div>
          </div>
          <div className={`flex-1 h-1 ${step >= 4 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-3967" data-mp="3u6uj0v5b9hi5"></div>
          <div className="flex-1" data-id="element-3968" data-mp="3p6g6azsrljds">
            <div className={`flex items-center ${step >= 4 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-3969" data-mp="1zt2yv1pcvf8g">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 4 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-3970" data-mp="1uvte3zcwvrqy">
                4
              </div>
              <div className="ml-3" data-id="element-3971" data-mp="fb0x9eaxhioa">
                <div className="text-sm font-medium" data-id="element-3972" data-mp="mnvu1t2n7me">
                  {t('pages.BillFinancingWizard.review_submit')}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Step 1: Bill Details */}
        {step === 1 && <div className="space-y-6" data-id="element-3973" data-mp="3nq2g8cs3qmcf">
            <div data-id="element-3974" data-mp="3sq708cs7xz7e">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-3975" data-mp="3sqqenjinu5go">
                {t('pages.BillFinancingWizard.bill_information')}
              </h2>
              <p className="text-gray-600" data-id="element-3976" data-mp="3dvk9eyre2aoe">
                {t('pages.BillFinancingWizard.review_the_bill_details_for_financing')}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 space-y-4" data-id="element-3977" data-mp="1c005wuffcmh0">
              <div className="grid grid-cols-2 gap-4" data-id="element-3978" data-mp="1h0e2ngbks9zq">
                <div data-id="element-3979" data-mp="1fka0mbceda1f">
                  <div className="text-sm text-gray-500" data-id="element-3980" data-mp="1fks49t1e2qjd">
                    {t('pages.BillFinancingWizard.bill_number')}
                  </div>
                  <div className="text-base font-medium text-gray-900" data-id="element-3981" data-mp="1an0fv8zydmjx">
                    {bill.billNumber}
                  </div>
                </div>
                <div data-id="element-3982" data-mp="11l0ts2a2zsa4">
                  <div className="text-sm text-gray-500" data-id="element-3983" data-mp="11lshys1cmnqd">
                    {t('pages.BillFinancingWizard.supplier')}
                  </div>
                  <div className="text-base font-medium text-gray-900" data-id="element-3984" data-mp="1tbf6vwa9t0kq">
                    {bill.supplierName}
                  </div>
                </div>
                <div data-id="element-3985" data-mp="1oim258esxs8n">
                  <div className="text-sm text-gray-500" data-id="element-3986" data-mp="1oi2klst2d5v5">
                    {t('pages.BillDetail.due_date')}
                  </div>
                  <div className="text-base font-medium text-gray-900" data-id="element-3987" data-mp="2h0znw9mv5nrd">
                    {new Date(bill.dueDate).toLocaleDateString('en-GB')}
                  </div>
                </div>
                <div data-id="element-3988" data-mp="3uxaq65is1kgg">
                  <div className="text-sm text-gray-500" data-id="element-3989" data-mp="3uy2p9qbl20bh">
                    {t('pages.BillFinancingWizard.bill_amount')}
                  </div>
                  <div className="text-2xl font-bold text-gray-900" data-id="element-3990" data-mp="3saycopzejnm">
                    {bill.currency} {bill.amount.toLocaleString('en-US')}
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-3991" data-mp="hqr6mk3hsi7v">
              <div className="flex items-start gap-3" data-id="element-3992" data-mp="mpc6ujiqv1wb">
                <DollarSignIcon size={20} className="text-blue-600 mt-0.5" data-id="element-3993" data-mp="mnrd2w497mzi" />
                <div className="text-sm text-blue-800" data-id="element-3994" data-mp="rfso6epor7ny">
                  <div className="font-medium mb-1" data-id="element-3995" data-mp="rieu69wxhf42">
                    {t('pages.BillFinancingWizard.bill_financing_benefits')}
                  </div>
                  <ul className="list-disc list-inside space-y-1" data-id="element-3996" data-mp="10i1uesdx8evj">
                    <li data-id="element-3997" data-mp="15faewzmjkuzy">
                      {t('pages.BillFinancingWizard.improve_cash_flow_by_paying_suppliers')}
                    </li>
                    <li data-id="element-3998" data-mp="bdts9rmakiqp">
                      {t('pages.BillFinancingWizard.maintain_good_supplier_relationships')}
                    </li>
                    <li data-id="element-3999" data-mp="lbrek9klc5go">
                      {t('pages.BillFinancingWizard.take_advantage_of_early_payment_discounts')}
                    </li>
                    <li data-id="element-4000" data-mp="3osgehcpo4gxd">
                      {t('pages.BillFinancingWizard.flexible_repayment_terms')}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="flex justify-end" data-id="element-4001" data-mp="1o0j0x9mgfpm">
              <button onClick={() => setStep(2)} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-id="element-4002" data-mp="1m7atdjd1nfi">
                {t('pages.BillFinancingWizard.continue')}
              </button>
            </div>
          </div>}
        {/* Step 2: Select Provider */}
        {step === 2 && <div className="space-y-6" data-id="element-4003" data-mp="30pa1x4ta725q">
            <div data-id="element-4004" data-mp="30qbdrbi4fsoi">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4005" data-mp="30quvar3v0f20">
                {t('pages.BillFinancingWizard.select_pomelo_financing_package')}
              </h2>
              <p className="text-gray-600" data-id="element-4006" data-mp="2qzqhhupx708r">
                {t('pages.BillFinancingWizard.choose_a_financing_package_that_best')}
              </p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6" data-id="element-4007" data-mp="3jmbat0pyhmey">
              <div className="flex items-start gap-3" data-id="element-4008" data-mp="34pbtgl5ayf7a">
                <BuildingIcon size={20} className="text-blue-600 mt-0.5" data-id="element-4009" data-mp="34spyy299j4iy" />
                <div data-id="element-4010" data-mp="39nlql1al7pvj">
                  <div className="font-semibold text-blue-900 mb-1" data-id="element-4011" data-mp="39r9gzrkqjw61">
                    {t('pages.BillFinancingWizard.pomelo_your_trusted_financial_partner')}
                  </div>
                  <div className="text-sm text-blue-800" data-id="element-4012" data-mp="23x9gtluouoy3">
                    {t('pages.BillFinancingWizard.as_a_valued_pomelo_customer_you')}
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-4" data-id="element-4013" data-mp="1u657p2rp1yb6">
              {dfccLoanPackages.map(pkg => <label key={pkg.id} className={`block p-6 border-2 rounded-lg cursor-pointer transition-all ${formData.loanPackage === pkg.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`} data-id="element-4014" data-mp="1z4q04g0pfdp2">
                  <input type="radio" name="loanPackage" value={pkg.id} checked={formData.loanPackage === pkg.id} onChange={e => setFormData({
              ...formData,
              loanPackage: e.target.value
            })} className="sr-only" data-id="element-4015" data-mp="1baooai2h86sy" />

                  <div className="flex items-start justify-between" data-id="element-4016" data-mp="u0kpxcpcauer">
                    <div className="flex-1" data-id="element-4017" data-mp="18opakm7ydg06">
                      <div className="flex items-center gap-2 mb-2" data-id="element-4018" data-mp="18pqw5kmlhkw5">
                        <div className="text-lg font-semibold text-gray-900" data-id="element-4019" data-mp="13y8ueucqzga4">
                          {pkg.name}
                        </div>
                        {formData.loanPackage === pkg.id && <CheckCircleIcon size={20} className="text-blue-600" data-id="element-4020" data-mp="1dobrp6qwjge9" />}
                      </div>
                      <p className="text-sm text-gray-600 mb-4" data-id="element-4021" data-mp="1mpia5ld431a5">
                        {pkg.description}
                      </p>
                      <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-4022" data-mp="3tf6io3es6nfz">
                        <div data-id="element-4023" data-mp="3oh52bq8qp287">
                          <div className="text-gray-500" data-id="element-4024" data-mp="3ohy2u9h3uvhw">
                            {t('pages.BillFinancingWizard.interest_rate')}
                          </div>
                          <div className="font-medium text-gray-900" data-id="element-4025" data-mp="1xcl1rjezrt9y">
                            {pkg.interestRate}
                          </div>
                        </div>
                        <div data-id="element-4026" data-mp="26czrqw2whwjs">
                          <div className="text-gray-500" data-id="element-4027" data-mp="26e1cxtdmrzf8">
                            {t('pages.BillFinancingWizard.max_term')}
                          </div>
                          <div className="font-medium text-gray-900" data-id="element-4028" data-mp="2ga4d4tox9nzj">
                            {pkg.maxTerm}
                          </div>
                        </div>
                        <div data-id="element-4029" data-mp="2l3qj630kytmd">
                          <div className="text-gray-500" data-id="element-4030" data-mp="2p3hi4u09gg1z">
                            {t('pages.BillFinancingWizard.processing_time')}
                          </div>
                          <div className="font-medium text-gray-900" data-id="element-4031" data-mp="2u07up7cuwtin">
                            {pkg.processingTime}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </label>)}
            </div>
            <div className="flex justify-between" data-id="element-4032" data-mp="2z6weqvmkm4cz">
              <button onClick={() => setStep(1)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-4033" data-mp="2z7emopili70q">
                {t('pages.BillApprovalWizard.back')}
              </button>
              <button onClick={() => setStep(3)} disabled={!formData.loanPackage} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4034" data-mp="3mwh53zl203wa">
                {t('pages.BillFinancingWizard.continue')}
              </button>
            </div>
          </div>}
        {/* Step 3: Financing Terms */}
        {step === 3 && <div className="space-y-6" data-id="element-4035" data-mp="9oe1sdkwm8h6">
            <div data-id="element-4036" data-mp="3qdbk13jtdy7w">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4037" data-mp="3qcsbuejyuo72">
                {t('pages.BillFinancingWizard.financing_terms')}
              </h2>
              <p className="text-gray-600" data-id="element-4038" data-mp="3vabppdcr4yyh">
                {t('pages.BillFinancingWizard.specify_the_financing_amount_and_terms')}
              </p>
            </div>
            <div className="space-y-6" data-id="element-4039" data-mp="3lgbdysmysvgs">
              <div data-id="element-4040" data-mp="cxx5qhm1ukfp">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-4041" data-mp="czgnkcnct3jl">
                  {t('pages.BillFinancingWizard.financing_amount_usd')}
                </label>
                <input type="number" value={formData.financingAmount} onChange={e => setFormData({
              ...formData,
              financingAmount: e.target.value
            })} placeholder={t('pages.BillFinancingWizard.attrs.enter_amount')} max={bill.amount} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-4042" data-mp="46n380t1vdyv" />

                <div className="mt-1 text-sm text-gray-500" data-id="element-4043" data-mp="2v2wvkxd6zb9l">
                  {t('pages.BillFinancingWizard.maximum')}{' '}
                  {bill.amount.toLocaleString('en-US')}
                </div>
              </div>
              <div data-id="element-4044" data-mp="22fswf90s5r5q">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-4045" data-mp="22hdp0t3jb00y">
                  {t('pages.BillFinancingWizard.requested_term_days')}
                </label>
                <select value={formData.requestedTerm} onChange={e => setFormData({
              ...formData,
              requestedTerm: e.target.value
            })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-4046" data-mp="2cf1plzntn1r1">
                  <option value="15" data-id="element-4047" data-mp="3uw3k1oal9ame">
                    {t('pages.BillFinancingWizard.15_days')}
                  </option>
                  <option value="30" data-id="element-4048" data-mp="3pj0sqqrz6zx">
                    {t('pages.BillFinancingWizard.30_days')}
                  </option>
                  <option value="45" data-id="element-4049" data-mp="8odfbghpm9e8">
                    {t('pages.BillFinancingWizard.45_days')}
                  </option>
                  <option value="60" data-id="element-4050" data-mp="374eq63i4ce3c">
                    {t('pages.BillFinancingWizard.60_days')}
                  </option>
                  <option value="90" data-id="element-4051" data-mp="3h4yrf65n1ilk">
                    {t('pages.BillFinancingWizard.90_days')}
                  </option>
                </select>
              </div>
              <div data-id="element-4052" data-mp="3lxi7qavpmru2">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-4053" data-mp="2og1ap7h2rtux">
                  {t('pages.BillFinancingWizard.purpose_of_financing')}
                </label>
                <select value={formData.purpose} onChange={e => setFormData({
              ...formData,
              purpose: e.target.value
            })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-4054" data-mp="2td01ph5fzkow">
                  <option value="" data-id="element-4055" data-mp="rl46xvlqg41e">
                    {t('pages.BillFinancingWizard.select_purpose')}
                  </option>
                  <option value="early_payment" data-id="element-4056" data-mp="mu5ksfe2cbq8">
                    {t('pages.BillFinancingWizard.early_payment_discount')}
                  </option>
                  <option value="cash_flow" data-id="element-4057" data-mp="iqg1a4c8wkc2">
                    {t('pages.BillFinancingWizard.cash_flow_management')}
                  </option>
                  <option value="supplier_relationship" data-id="element-4058" data-mp="8uw8hqsza1rj">
                    {t('pages.BillFinancingWizard.supplier_relationship')}
                  </option>
                  <option value="other" data-id="element-4059" data-mp="3xw2th01j10y">
                    {t('pages.BillFinancingWizard.other')}
                  </option>
                </select>
              </div>
              <div data-id="element-4060" data-mp="1wqsqgxnmghxn">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-4061" data-mp="1wozr83fc8zzo">
                  {t('pages.BillFinancingWizard.additional_notes')}
                </label>
                <textarea value={formData.additionalNotes} onChange={e => setFormData({
              ...formData,
              additionalNotes: e.target.value
            })} rows={4} placeholder={t('pages.BillFinancingWizard.attrs.any_additional_information')} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-4062" data-mp="1np2y9plx1eyt" />
              </div>
            </div>
            <div className="flex justify-between" data-id="element-4063" data-mp="bg6uyg36pdh4">
              <button onClick={() => setStep(2)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-4064" data-mp="bgyh70aw5yuq">
                {t('pages.BillApprovalWizard.back')}
              </button>
              <button onClick={() => setStep(4)} disabled={!formData.financingAmount || !formData.requestedTerm || !formData.purpose} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4065" data-mp="gf9pv7pga3bd">
                {t('pages.BillFinancingWizard.review_request')}
              </button>
            </div>
          </div>}
        {/* Step 4: Review & Submit */}
        {step === 4 && <div className="space-y-6" data-id="element-4066" data-mp="1e0xj5jqm05io">
            <div data-id="element-4067" data-mp="194gwktz25z7p">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4068" data-mp="19509fw9upxev">
                {t('pages.BillFinancingWizard.review_financing_request')}
              </h2>
              <p className="text-gray-600" data-id="element-4069" data-mp="1gnuekrjw4mtj">
                {t('pages.BillFinancingWizard.please_review_your_financing_request_before')}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 space-y-6" data-id="element-4070" data-mp="1ql8k3w49zr72">
              <div data-id="element-4071" data-mp="1qjxfxv6x4hff">
                <h3 className="text-sm font-medium text-gray-500 mb-3" data-id="element-4072" data-mp="12vxpyxhfkv1w">
                  {t('pages.BillFinancingWizard.bill_information_2')}
                </h3>
                <div className="grid grid-cols-2 gap-4" data-id="element-4073" data-mp="xyxjilgo5qa9">
                  <div data-id="element-4074" data-mp="1bor7l1kco6ix">
                    <div className="text-sm text-gray-500" data-id="element-4075" data-mp="1bq2br2hpjgak">
                      {t('pages.BillFinancingWizard.bill_number')}
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-4076" data-mp="j7ygrr2ilvyl">
                      {bill.billNumber}
                    </div>
                  </div>
                  <div data-id="element-4077" data-mp="f6cwvlrkchew">
                    <div className="text-sm text-gray-500" data-id="element-4078" data-mp="52oe67nbx63h">
                      {t('pages.BillFinancingWizard.supplier')}
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-4079" data-mp="9u6csp1vqula">
                      {bill.supplierName}
                    </div>
                  </div>
                </div>
              </div>
              <div className="pt-4 border-t border-gray-200" data-id="element-4080" data-mp="43a1x4yi29r">
                <h3 className="text-sm font-medium text-gray-500 mb-3" data-id="element-4081" data-mp="nsmlgljayv62">
                  {t('pages.BillFinancingWizard.financing_details')}
                </h3>
                <div className="grid grid-cols-2 gap-4" data-id="element-4082" data-mp="sq6560yrs05e">
                  <div data-id="element-4083" data-mp="dtygancf2ajs">
                    <div className="text-sm text-gray-500" data-id="element-4084" data-mp="duqbhx23q69j">
                      {t('pages.BillFinancingWizard.bank')}
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-4085" data-mp="2ykpfjvg0zv6v">
                      {t('pages.BillFinancingWizard.pomelo')}
                    </div>
                  </div>
                  <div data-id="element-4086" data-mp="1a1l652tm8e94">
                    <div className="text-sm text-gray-500" data-id="element-4087" data-mp="1a1unk20lhd37">
                      {t('pages.BillFinancingWizard.package')}
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-4088" data-mp="154l20xgbtlkn">
                      {selectedPackage?.name}
                    </div>
                  </div>
                  <div data-id="element-4089" data-mp="vbvrq5wiidqw">
                    <div className="text-sm text-gray-500" data-id="element-4090" data-mp="vcf291ju1zuw">
                      {t('pages.BillFinancingWizard.interest_rate')}
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-4091" data-mp="mbqv8qsuwzri">
                      {selectedPackage?.interestRate}
                    </div>
                  </div>
                  <div data-id="element-4092" data-mp="ce2o11e2dzsg">
                    <div className="text-sm text-gray-500" data-id="element-4093" data-mp="cem4serzai4w">
                      {t('pages.BillFinancingWizard.processing_time')}
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-4094" data-mp="2bokmbcm9knkx">
                      {selectedPackage?.processingTime}
                    </div>
                  </div>
                  <div data-id="element-4095" data-mp="2eb371u4wgicy">
                    <div className="text-sm text-gray-500" data-id="element-4096" data-mp="2jag3n6lkp3sq">
                      {t('pages.BillFinancingWizard.financing_amount')}
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-4097" data-mp="2o1odu4uq8t6y">
                      {t('pages.BillFinancingWizard.rs')}{' '}
                      {parseFloat(formData.financingAmount).toLocaleString('en-US')}
                    </div>
                  </div>
                  <div data-id="element-4098" data-mp="20dobs8rytgd2">
                    <div className="text-sm text-gray-500" data-id="element-4099" data-mp="25aolcto0x18x">
                      {t('pages.BillFinancingWizard.term')}
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-4100" data-mp="2aaj7yl0rn4o6">
                      {formData.requestedTerm}{' '}
                      {t('pages.BillFinancingWizard.days')}
                    </div>
                  </div>
                  <div data-id="element-4101" data-mp="3lqreexjbj054">
                    <div className="text-sm text-gray-500" data-id="element-4102" data-mp="3ja04v5xvakqi">
                      {t('pages.BillFinancingWizard.purpose')}
                    </div>
                    <div className="text-base font-medium text-gray-900 capitalize" data-id="element-4103" data-mp="3fa96oh60h2by">
                      {formData.purpose.replace('_', ' ')}
                    </div>
                  </div>
                </div>
              </div>
              {formData.additionalNotes && <div className="pt-4 border-t border-gray-200" data-id="element-4104" data-mp="62om7zps12mw">
                  <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-4105" data-mp="22ecqd2rzu3a">
                    {t('pages.BillFinancingWizard.additional_notes_2')}
                  </h3>
                  <p className="text-sm text-gray-700" data-id="element-4106" data-mp="fvnim3rilxh2">
                    {formData.additionalNotes}
                  </p>
                </div>}
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-4107" data-mp="ps8ossf9temh">
              <div className="flex items-start gap-3" data-id="element-4108" data-mp="ktp1oooxxwsj">
                <FileTextIcon size={20} className="text-blue-600 mt-0.5" data-id="element-4109" data-mp="14efue4ac1po4" />
                <div className="text-sm text-blue-800" data-id="element-4110" data-mp="14f7iku1lol4d">
                  {t('pages.BillFinancingWizard.by_submitting_this_request_you_agree')}{' '}
                  {selectedPackage?.name}
                  {t('pages.BillFinancingWizard.the_request_will_be_processed_within')}{' '}
                  {selectedPackage?.processingTime}.
                </div>
              </div>
            </div>
            <div className="flex justify-between" data-id="element-4111" data-mp="phyjtf9ywf2m">
              <button onClick={() => setStep(3)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-4112" data-mp="bpqntpqics4z">
                {t('pages.BillApprovalWizard.back')}
              </button>
              <button onClick={handleSubmit} className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700" data-id="element-4113" data-mp="1svsffpj77ux">
                {t('pages.BillFinancingWizard.submit_financing_request')}
              </button>
            </div>
          </div>}
      </div>
    </div>;
}